/**
 * Keyset (cursor) pagination codecs for search results.
 *
 * Cursors are delimited plaintext (NOT base64) — they are opaque pagination
 * tokens, not secrets, and plaintext keeps them parseable across server/client
 * with no Buffer/btoa dependency. The tuple format is byte-for-byte the same as
 * the marketplace's pre-existing `<created_at>__<id>` cursor, so old in-flight
 * Load-More links keep working.
 *
 * Separator is `__`. None of the encoded fields can contain `__`:
 * an ISO-8601 timestamp and a UUID never do, and a `ts_rank_cd` real is a plain
 * number.
 */

const SEP = '__';

export interface TupleCursor {
  /** ISO-8601 timestamp string. */
  createdAt: string;
  /** UUID. */
  id: string;
}

export interface RankedCursor extends TupleCursor {
  /** `ts_rank_cd` relevance score of the last row on the page. */
  rank: number;
}

/** `<createdAt>__<id>` — newest-first browse pagination. */
export function encodeTupleCursor(cursor: TupleCursor): string {
  return `${cursor.createdAt}${SEP}${cursor.id}`;
}

/** Parse a tuple cursor. Returns null when malformed. */
export function decodeTupleCursor(raw: string): TupleCursor | null {
  const sep = raw.indexOf(SEP);
  if (sep === -1) return null;
  const createdAt = raw.slice(0, sep);
  const id = raw.slice(sep + SEP.length);
  if (!createdAt || !id) return null;
  return { createdAt, id };
}

/** `<rank>__<createdAt>__<id>` — relevance-ranked search pagination. */
export function encodeRankCursor(cursor: RankedCursor): string {
  return `${cursor.rank}${SEP}${cursor.createdAt}${SEP}${cursor.id}`;
}

/**
 * Parse a ranked cursor. Returns null when malformed (wrong arity, non-finite
 * rank, or missing parts).
 */
export function decodeRankCursor(raw: string): RankedCursor | null {
  const i1 = raw.indexOf(SEP);
  if (i1 === -1) return null;
  const i2 = raw.indexOf(SEP, i1 + SEP.length);
  if (i2 === -1) return null;

  const rankStr = raw.slice(0, i1);
  const createdAt = raw.slice(i1 + SEP.length, i2);
  const id = raw.slice(i2 + SEP.length);

  const rank = Number(rankStr);
  if (!Number.isFinite(rank) || !createdAt || !id) return null;
  // Reject a stray 4th segment (would have landed inside `id`).
  if (id.includes(SEP)) return null;

  return { rank, createdAt, id };
}
